
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { VideoRecording } from '@/types';
import { Button } from '@/components/ui/button';
import { Trash2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import VideoPlayer from './VideoPlayer';
import SocialSharePanel from '../SocialSharePanel';

interface RecordingsTabProps {
  userId: string | null;
}

// Extend VideoRecording type to include merchant_id if not already defined in @/types
interface ExtendedVideoRecording extends VideoRecording {
  merchant_id?: string | null;
}

const RecordingsTab: React.FC<RecordingsTabProps> = ({ userId }) => {
  const [recordings, setRecordings] = useState<ExtendedVideoRecording[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showShareDialog, setShowShareDialog] = useState<boolean>(false);
  const [selectedRecording, setSelectedRecording] = useState<ExtendedVideoRecording | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>('all');

  useEffect(() => {
    if (userId) {
      fetchRecordings();
    }
  }, [userId]);

  const fetchRecordings = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('recordings')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        toast.error('Failed to load recordings');
        console.error('Error fetching recordings:', error);
        return;
      }

      setRecordings(data || []);
    } catch (error) {
      console.error('Error in fetchRecordings:', error);
      toast.error('Failed to load recordings');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (recordingId: string | number) => {
    try {
      // First delete the video file from storage
      const videoToDelete = recordings.find(r => r.id === recordingId);
      if (videoToDelete?.video_path) {
        // Extract file path from the URL
        const url = new URL(String(videoToDelete.video_path));
        const pathnameParts = url.pathname.split('/');
        const startIndex = pathnameParts.indexOf('object') + 2; // Skip 'object' and 'public'
        const storagePath = pathnameParts.slice(startIndex).join('/');
        
        if (storagePath) {
          console.log('Removing storage file:', storagePath);
          const { error: storageError } = await supabase.storage
            .from('videos')
            .remove([storagePath]);
          
          if (storageError) {
            console.error('Error removing video file:', storageError);
            // Continue anyway to try to delete the database record
          }
        }
      }

      // Then delete the database record
      const { error } = await supabase
        .from('recordings')
        .delete()
        .eq('id', recordingId);

      if (error) {
        toast.error('Failed to delete recording');
        console.error('Error deleting recording:', error);
        return;
      }

      toast.success('Recording deleted successfully');
      setRecordings(recordings.filter(r => r.id !== recordingId));
    } catch (error) {
      console.error('Error in handleDelete:', error);
      toast.error('Failed to delete recording');
    }
  };

  const handleShare = (recording: ExtendedVideoRecording) => {
    setSelectedRecording(recording);
    setShowShareDialog(true);
  };

  const closeShareDialog = () => {
    setShowShareDialog(false);
    setSelectedRecording(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2 mb-4">
        <Button 
          variant={selectedTab === 'all' ? "default" : "outline"}
          onClick={() => setSelectedTab('all')}
        >
          All Recordings
        </Button>
        <Button 
          variant={selectedTab === 'recent' ? "default" : "outline"}
          onClick={() => setSelectedTab('recent')}
        >
          Recent
        </Button>
      </div>
      
      {recordings.length === 0 ? (
        <div className="text-center p-8">
          <p>No recordings found. Start recording to see your videos here!</p>
          <Button className="mt-4" onClick={() => window.location.href = '/record'}>
            Record a Video
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {recordings.map((recording) => (
            <div 
              key={recording.id} 
              className="bg-gray-900 rounded-lg overflow-hidden shadow-lg"
            >
              <VideoPlayer 
                id={recording.id}
                title={recording.title}
                video_path={String(recording.video_path)}
                avatar_path={recording.avatar_path}
                emotion={recording.emotion}
                location={recording.location}
                created_at={recording.created_at}
                user_id={recording.user_id}
                merchant_id={recording.merchant_id}
              />
              <div className="p-4">
                <div className="flex justify-between items-center">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleShare(recording)}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={() => handleDelete(recording.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {showShareDialog && selectedRecording && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-lg w-full">
            <h2 className="text-xl font-bold mb-4">Share Recording</h2>
            <p className="mb-4">Select a platform to share your recording:</p>
            
            <SocialSharePanel
              videoId={Number(selectedRecording.id)}
              userId={selectedRecording.user_id}
              videoUrl={String(selectedRecording.video_path)}
              title={String(selectedRecording.title) || "Check out my video!"}
              description={"Recorded with emotion: " + 
                (selectedRecording.emotion?.dominantEmotion || "neutral")}
              merchantId={selectedRecording.merchant_id || undefined}
              isOpen={true}
              onClose={closeShareDialog}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordingsTab;
